import React, { useEffect, useState, useRef, useCallback } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShoppingCart,
  faDollarSign,
  faEye,
  faHeart,
  faShare,
  faTrophy,
  faCopy,
  faCheck
} from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router-dom";
import { COLOR_APP, COLOR_TEXT } from '../../../constants';
import moment from "moment";

function LiveMarathonPublic() {
  const { liveId } = useParams();
  
  // Audio refs for sound notifications
  const orderSoundRef = useRef(null);
  const revenueSoundRef = useRef(null);
  
  // Mock data for public display
  const [liveData, setLiveData] = useState({
    id: liveId || '1',
    title: 'Super Flash Sale Marathon 🔥',
    status: 'LIVE',
    createdAt: moment().subtract(2, 'hours').toISOString(),
    description: 'Amazing deals with up to 70% off on selected items!',
    viewers: 1247,
    likes: 892,
    shares: 156
  });
  
  const [stats, setStats] = useState({
    totalOrders: 342,
    totalRevenue: 15750000
  });
  
  const [currentTime, setCurrentTime] = useState(moment());
  const [copySuccess, setCopySuccess] = useState(false);

  // Sound notification function
  const playNotificationSound = useCallback((type) => {
    try {
      let audioRef;
      switch (type) {
        case 'order':
          audioRef = orderSoundRef;
          break;
        case 'revenue':
          audioRef = revenueSoundRef;
          break;
        default:
          return;
      }
      
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => console.log('Audio play failed:', e));
      }
    } catch (error) {
      console.log('Sound notification error:', error);
    }
  }, []);

  // Auto-update statistics simulation (replacing useSubscription with interval)
  useEffect(() => {
    const updateInterval = setInterval(() => {
      setStats(prevStats => {
        const newStats = { ...prevStats };
        let hasChanges = false;
        
        // Randomly update orders (increase more often than decrease)
        if (Math.random() < 0.9) { // 90% chance to update orders
          const orderChange = Math.random() < 0.88 ? 
            Math.floor(Math.random() * 5) + 1 : // 88% chance to increase by 1-5
            -Math.floor(Math.random() * 3) - 1; // 12% chance to decrease by 1-3
          
          newStats.totalOrders = Math.max(0, prevStats.totalOrders + orderChange);
          if (orderChange !== 0) {
            hasChanges = true;
            setTimeout(() => playNotificationSound('order'), 100);
          }
        }
        
        // Update revenue based on orders (more frequent updates)
        if (Math.random() < 0.85) { // 85% chance to update revenue
          const revenueChange = Math.random() < 0.92 ? 
            Math.floor(Math.random() * 800000) + 200000 : // 92% chance to increase by 200k-1M
            -Math.floor(Math.random() * 300000) - 100000; // 8% chance to decrease by 100k-400k
          
          newStats.totalRevenue = Math.max(0, prevStats.totalRevenue + revenueChange);
          if (revenueChange !== 0) {
            hasChanges = true;
            setTimeout(() => playNotificationSound('revenue'), 200);
          }
        }
        
        return hasChanges ? newStats : prevStats;
      });
    }, 2000); // Update every 2 seconds for more dynamic feel

    return () => clearInterval(updateInterval);
  }, [playNotificationSound]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(moment());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const getTimeDifference = () => {
    if (!liveData?.createdAt) return "00:00:00";
    
    const startTime = moment(liveData.createdAt);
    const duration = moment.duration(currentTime.diff(startTime));
    
    const hours = Math.floor(duration.asHours()).toString().padStart(2, '0');
    const minutes = duration.minutes().toString().padStart(2, '0');
    const seconds = duration.seconds().toString().padStart(2, '0');
    
    return `${hours}:${minutes}:${seconds}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('lo-LA', {
      style: 'currency',
      currency: 'LAK',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Copy link to clipboard function
  const copyLinkToClipboard = useCallback(async () => {
    try {
      const currentUrl = window.location.href;
      await navigator.clipboard.writeText(currentUrl);
      setCopySuccess(true);
      
      // Reset success state after 2 seconds
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopySuccess(true);
        setTimeout(() => {
          setCopySuccess(false);
        }, 2000);
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
      }
      document.body.removeChild(textArea);
    }
  }, []);

  return (
    <div style={{ 
      minHeight: "100vh", 
      background: COLOR_APP,
      padding: "0",
      overflow: "hidden"
    }}>
      {/* Website Logo/Header */}
      <div style={{
        position: "absolute",
        top: "20px",
        left: "20px",
        zIndex: 1000,
        background: "rgba(255,255,255,0.95)",
        borderRadius: "15px",
        padding: "10px 20px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        backdropFilter: "blur(10px)"
      }}>
        <div className="d-flex align-items-center">
          <img 
            src="/4bLogo.png" 
            alt="4B Logo" 
            style={{
              height: "40px",
              width: "auto",
              marginRight: "12px"
            }}
          />
          <div>
            <h6 style={{ margin: 0, color: "#2c3e50", fontWeight: "700", fontSize: "16px" }}>
              4B Live Marathon
            </h6>
            <p style={{ margin: 0, color: "#7f8c8d", fontSize: "12px" }}>
              Live Marathon
            </p>
          </div>
        </div>
      </div>

      {/* Live Status Badge */}
      <div style={{
        position: "absolute",
        top: "20px",
        right: "20px",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        gap: "10px"
      }}>
        {/* Copy Link Button */}
        <Button
          onClick={copyLinkToClipboard}
          style={{
            background: copySuccess 
              ? "linear-gradient(135deg, #27ae60, #2ecc71)" 
              : "linear-gradient(135deg, #3498db, #2980b9)",
            border: "none",
            borderRadius: "20px",
            padding: "8px 16px",
            color: "white",
            fontSize: "14px",
            fontWeight: "600",
            boxShadow: copySuccess 
              ? "0 4px 15px rgba(39,174,96,0.4)" 
              : "0 4px 15px rgba(52,152,219,0.4)",
            backdropFilter: "blur(10px)",
            transition: "all 0.3s ease",
            display: "flex",
            alignItems: "center",
            gap: "6px"
          }}
          title={copySuccess ? "Link copied!" : "Copy share link"}
        >
          <FontAwesomeIcon icon={copySuccess ? faCheck : faCopy} />
          {copySuccess ? "Copied!" : "Copy Link"}
        </Button>

        {/* Live Status */}
        <div style={{
          background: "rgba(255,59,48,0.95)",
          color: "white",
          padding: "8px 16px",
          borderRadius: "20px",
          fontSize: "14px",
          fontWeight: "600",
          boxShadow: "0 4px 15px rgba(255,59,48,0.4)",
          backdropFilter: "blur(10px)",
          animation: "pulse 2s infinite"
        }}>
          🔴 LIVE • {getTimeDifference()}
        </div>
      </div>

      {/* Full Screen Stats Container */}
      <Container fluid className="h-100 d-flex align-items-center justify-content-center p-0">
        <Row className="w-100 h-100 g-0">
          {/* Total Orders - Left Half */}
          <Col xs={12} md={6} className="h-100">
            <Card style={{
              height: "100vh",
              background: COLOR_APP,
              border: "none",
              borderRadius: "0",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden"
            }}>
              {/* Background Pattern */}
              <div style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                opacity: 0.3
              }} />
              
              <Card.Body className="text-center position-relative" style={{ zIndex: 2 }}>
                <div style={{
                  background: "rgba(255,255,255,0.2)",
                  width: "120px",
                  height: "120px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 40px",
                  fontSize: "60px",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                  backdropFilter: "blur(10px)"
                }}>
                  <FontAwesomeIcon icon={faShoppingCart} />
                </div>
                
                <h1 style={{ 
                  fontSize: "clamp(4rem, 12vw, 8rem)",
                  fontWeight: "900",
                  margin: "0 0 20px 0",
                  textShadow: "0 4px 8px rgba(0,0,0,0.3)",
                  animation: "countUp 0.5s ease-out",
                  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
                }}>
                  {stats.totalOrders.toLocaleString()}
                </h1>
                
                <h2 style={{ 
                  fontSize: "clamp(1.5rem, 4vw, 3rem)",
                  fontWeight: "700",
                  margin: 0,
                  opacity: 0.9,
                  textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
                }}>
                  Total Orders
                </h2>
                
                <div style={{
                  marginTop: "30px",
                  fontSize: "clamp(1rem, 3vw, 1.5rem)",
                  fontWeight: "600",
                  opacity: 0.8,
                  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
                }}>
                  🚀 Live Updates
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Total Revenue - Right Half */}
          <Col xs={12} md={6} className="h-100">
            <Card style={{
              height: "100vh",
              background: COLOR_APP,
              border: "none",
              borderRadius: "0",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden"
            }}>
              {/* Background Pattern */}
              <div style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M20 20c0-11.046-8.954-20-20-20v20h20z'/%3E%3C/g%3E%3C/svg%3E")`,
                opacity: 0.2
              }} />
              
              <Card.Body className="text-center position-relative" style={{ zIndex: 2 }}>
                <div style={{
                  background: "rgba(255,255,255,0.2)",
                  width: "120px",
                  height: "120px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 40px",
                  fontSize: "60px",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                  backdropFilter: "blur(10px)"
                }}>
                  <FontAwesomeIcon icon={faDollarSign} />
                </div>
                
                <h1 style={{ 
                  fontSize: "clamp(2.5rem, 8vw, 5rem)",
                  fontWeight: "900",
                  margin: "0 0 20px 0",
                  textShadow: "0 4px 8px rgba(0,0,0,0.3)",
                  animation: "countUp 0.5s ease-out",
                  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
                }}>
                  {formatCurrency(stats.totalRevenue)}
                </h1>
                
                <h2 style={{ 
                  fontSize: "clamp(1.5rem, 4vw, 3rem)",
                  fontWeight: "700",
                  margin: 0,
                  opacity: 0.9,
                  textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
                }}>
                  Total Revenue
                </h2>
                
                <div style={{
                  marginTop: "30px",
                  fontSize: "clamp(1rem, 3vw, 1.5rem)",
                  fontWeight: "600",
                  opacity: 0.8,
                  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
                }}>
                  💰 Real-time Sales
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Audio Elements for Sound Notifications */}
      <audio ref={orderSoundRef} preload="auto">
        <source src="/assets/audio/notic_v2.mp3" type="audio/mpeg" />
      </audio>
      
      <audio ref={revenueSoundRef} preload="auto">
        <source src="/assets/audio/notic_v2.mp3" type="audio/mpeg" />
      </audio>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        
        @keyframes countUp {
          0% { transform: scale(0.8); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        .h-100 {
          height: 100vh !important;
        }
        
        @media (max-width: 768px) {
          .h-100 {
            height: 50vh !important;
          }
        }
      `}</style>
    </div>
  );
}

export default LiveMarathonPublic;